import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Stack } from '@mui/material';
import { Info, Warning } from '@mui/icons-material';
import { getIn } from 'formik';

import * as S from './style';
import CustomLabel from '../CustomLabel';
import CustomTooltip from '../CustomTooltip';
import ErrorFeedbackP from '../ErrorFeedbackP';

const CustomSelect = ({
  value,
  onChange,
  onBlur,
  label,
  id,
  options,
  optionsGroups,
  disabled,
  hideSelect,
  info,
  touched,
  errors,
  required,
  isLoading,
  noLabel,
  sx,
}) => {
  const { t } = useTranslation();

  const touchedField = getIn(touched, id);
  const errorsField = getIn(errors, id);

  const displayNotFound = useMemo(
    () =>
      !isLoading &&
      value &&
      !options.some(o => o.value === value) &&
      !optionsGroups.some(g => g.list.some(o => o.value === value)),
    [isLoading, value, options, optionsGroups],
  );

  const displayOptions = useMemo(() => {
    if (displayNotFound) {
      return [
        ...options,
        { value, label: `${label} ${t('components.select.unknown')}` },
      ];
    }
    return options;
  }, [displayNotFound, label, options, t, value]);

  return (
    <Box style={!noLabel ? { width: '100%' } : { width: '180px' }} sx={sx}>
      {!noLabel && (
        <Stack direction="row" alignItems="center">
          <CustomLabel htmlFor={id} required={required}>
            {label}
          </CustomLabel>
          {info && (
            <CustomTooltip title={info} placement="top">
              <Info sx={{ marginLeft: '10px', marginTop: '8px' }} />
            </CustomTooltip>
          )}
        </Stack>
      )}
      <Stack direction="row" alignItems="center">
        <S.StyledSelect
          $requiredColor={noLabel && touchedField && errorsField}
          id={id}
          name={id}
          onChange={onChange}
          value={value}
          disabled={disabled || isLoading}
          onBlur={onBlur}
        >
          {!hideSelect && (
            <option value="">
              {noLabel
                ? label
                : t('configuration_menu.rules_trigger.select', { label })}
            </option>
          )}
          {displayOptions?.map(opt => (
            <option
              style={{ color: 'black' }}
              key={opt.value}
              value={opt.value}
            >
              {opt.label}
            </option>
          ))}
          {optionsGroups?.map(group => (
            <optgroup key={group.category.value} label={group.category.label}>
              {group.list.map?.(opt => (
                <option
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                >
                  {opt.label}
                </option>
              ))}
            </optgroup>
          ))}
        </S.StyledSelect>
        {displayNotFound && (
          <CustomTooltip
            title={t('components.select.unknown_info')}
            placement="top"
          >
            <Warning sx={{ marginLeft: '5px', zIndex: 1 }} />
          </CustomTooltip>
        )}
        {isLoading && <CircularProgress size={20} sx={{ marginLeft: '5px' }} />}
      </Stack>
      {!noLabel && (
        <ErrorFeedbackP touched={touchedField} error={errorsField} />
      )}
    </Box>
  );
};

CustomSelect.defaultProps = {
  disabled: false,
  hideSelect: false,
  required: false,
  noLabel: false,
  info: '',
  touched: {},
  errors: {},
  onBlur: () => null,
  options: [],
  optionsGroups: [],
};

CustomSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  options: PropTypes.array,
  optionsGroups: PropTypes.array,
  value: PropTypes.any.isRequired,
  disabled: PropTypes.bool,
  hideSelect: PropTypes.bool,
  required: PropTypes.bool,
  isLoading: PropTypes.bool,
  noLabel: PropTypes.bool,
  info: PropTypes.string,
  touched: PropTypes.object,
  errors: PropTypes.object,
  sx: PropTypes.object,
};

export default CustomSelect;
